import {FISH_VOICES,FISH_DEFAULT_VOICE} from '@vgen/contracts';
import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import {
  ArrowLeft,
  ChevronDown,
  Clapperboard,
  Copy,
  Film,
  FolderKanban,
  HelpCircle,
  Images,
  MessageSquare,
  Mic2,
  MoreHorizontal,
  Play,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from "lucide-react";
import { formatCredits } from "@vgen/contracts";
import {
  autoBoundary,
  eligibleFor,
  missingFor,
  nextWork,
  stageNames,
} from "./studio-readiness";
import { apiRequest as api } from "../api/client";
import type { MediaAsset, Model } from "../types";
import { AppDialog } from "../shared/AppDialog";
import { ScenarioWorkspace as ScenarioWorkspaceView } from "./ScenarioWorkspace";
import {
  ScenarioOnboarding,
  type ScenarioKickoffMode,
  type ScenarioSourceMode,
  type ScenarioTemplateChoice,
} from "./ScenarioOnboarding";
import { SceneCastEditor } from "./SceneCastEditor";
import { StoryboardCastStrip } from "./StoryboardCastStrip";
import { FinalReview } from "./FinalReview";
import type {
  ProjectCharacter,
  ScenarioMessage,
  ScenarioProposal,
  ScenarioTextModel,
  ScenarioVersion,
  SceneCharacterAssignment,
} from "./scenario-types";
import "./studio-workspace.css";

type DropdownOption = {
  value: string;
  label: string;
  note?: string;
  group?: string;
};
function Dropdown({
  label,
  value,
  options,
  onChange,
  className = "",
  disabled = false,
  children,
}: {
  label: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected =
    options.find((option) => option.value === value) ?? options[0];
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const groups = [
    ...new Set(options.map((option) => option.group).filter(Boolean)),
  ] as string[];
  return (
    <div ref={root} className={`dropdown ${className} ${open ? "open" : ""}`}>
      <button
        type="button"
        {...(!disabled
          ? { role: "combobox", "aria-expanded": open, "aria-controls": listId }
          : {})}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        {children ?? <span>{selected?.label}</span>}
        {!disabled && <ChevronDown />}
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={label}
          className="dropdown-menu grouped-model-menu"
        >
          {(groups.length ? groups : [undefined]).map((group) => (
            <div className="model-family" key={group ?? "all"}>
              {group && <div className="model-family-title">{group}</div>}
              {options
                .filter((option) => !group || option.group === group)
                .map((option) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    key={option.value}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span>{option.label}</span>
                    {option.note && <small>{option.note}</small>}
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectActionsMenu({
  onTemplate,
  onCopy,
  onDelete,
}: {
  onTemplate: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };
  return (
    <div className="studio-project-actions" ref={root}>
      <button
        type="button"
        className="studio-project-menu-trigger"
        aria-label="Действия проекта"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreHorizontal />
      </button>
      {open && (
        <div
          id={menuId}
          className="studio-project-menu-popover"
          role="menu"
          aria-label="Действия проекта"
        >
          <button type="button" role="menuitem" onClick={() => choose(onTemplate)}>
            <Save />
            Сохранить настройки как шаблон
          </button>
          <button type="button" role="menuitem" onClick={() => choose(onCopy)}>
            <Copy />
            Создать копию проекта
          </button>
          <button type="button" className="danger" role="menuitem" onClick={() => choose(onDelete)}>
            <Trash2 />
            Удалить проект
          </button>
        </div>
      )}
    </div>
  );
}

function ProjectListItem({project,onOpen,onCopy,onDelete}:{project:StudioProject;onOpen:()=>void;onCopy:()=>void;onDelete:()=>void}){
  const [open,setOpen]=useState(false);const timer=useRef<number|null>(null);const start=useRef<{x:number;y:number}|null>(null);const suppressClick=useRef(false);const root=useRef<HTMLDivElement>(null);
  const cancel=()=>{if(timer.current!==null)window.clearTimeout(timer.current);timer.current=null;start.current=null};
  const begin=(event:ReactPointerEvent)=>{if(event.pointerType==='mouse'||event.button!==0)return;start.current={x:event.clientX,y:event.clientY};timer.current=window.setTimeout(()=>{suppressClick.current=true;setOpen(true);navigator.vibrate?.(20)},520)};
  const move=(event:ReactPointerEvent)=>{if(start.current&&Math.hypot(event.clientX-start.current.x,event.clientY-start.current.y)>10)cancel()};
  const choose=(action:()=>void)=>{setOpen(false);action()};
  useEffect(()=>{if(!open)return;const close=(event:PointerEvent)=>{if(!root.current?.contains(event.target as Node))setOpen(false)};const key=(event:KeyboardEvent)=>{if(event.key==='Escape')setOpen(false)};const back=()=>setOpen(false);document.addEventListener('pointerdown',close);document.addEventListener('keydown',key);window.addEventListener('popstate',back);return()=>{document.removeEventListener('pointerdown',close);document.removeEventListener('keydown',key);window.removeEventListener('popstate',back)}},[open]);
  return <div ref={root} className="studio-project-item" onContextMenu={event=>{event.preventDefault();setOpen(true)}} onPointerDown={begin} onPointerMove={move} onPointerUp={cancel} onPointerCancel={cancel}>
    <button onClick={()=>{if(suppressClick.current){suppressClick.current=false;return}onOpen()}}>
      <span className={`project-ratio ratio-${project.aspectRatio.replace(":", "-")}`}>{project.aspectRatio}</span>
      <span><b>{project.title}</b>{project.topic&&<small>{project.topic}</small>}</span>
      <span><em>{studioStatus[project.status]??project.status}</em>{Number(project.totalDurationMs)>0&&<small>{Math.round(Number(project.totalDurationMs)/1000)} сек.</small>}</span>
    </button>
    <button type="button" className="studio-project-item-menu" aria-label={`Действия проекта ${project.title}`} aria-expanded={open} onClick={()=>setOpen(value=>!value)}><MoreHorizontal/></button>
    {open&&<div className="studio-project-context" role="menu" aria-label={`Действия: ${project.title}`}>
      <button role="menuitem" onClick={()=>choose(onCopy)}><Copy/>Дублировать</button>
      <button className="danger" role="menuitem" onClick={()=>choose(onDelete)}><Trash2/>Удалить</button>
      <button className="secondary" onClick={()=>setOpen(false)}>Отмена</button>
    </div>}
  </div>;
}

function VoicePicker({title,value,options,onChange}:{title:string;value:string;options:DropdownOption[];onChange:(value:string)=>void}){
  const [help,setHelp]=useState(false);const selected=options.find(option=>option.value===value)??options[0];
  return <div className="voice-picker"><span>{title}</span><div><Dropdown label={`Голос: ${title}`} value={value} options={options} onChange={onChange}/><button type="button" aria-label={`О голосе: ${title}`} onClick={()=>setHelp(true)}><HelpCircle/></button></div>{help&&<AppDialog title={selected?.label??'Голос'} onClose={()=>setHelp(false)}><p>{FISH_VOICES.find(v=>v.id===value)?.description??'Русскоязычный голос Fish Audio.'} Описание из каталога провайдера. Перед публикацией прослушайте результат.</p><p className="muted">Назначение сохраняется в проекте: персонаж продолжит говорить этим голосом, пока вы не выберете другой.</p></AppDialog>}</div>;
}

function voiceAssignmentSummary(value:Record<string,unknown>,options:DropdownOption[]){
  const label=(entry:unknown)=>{const id=entry&&typeof entry==='object'?String((entry as Record<string,unknown>).voiceId??''):'';return options.find(option=>option.value===id)?.label??'Сохранённый голос'};
  const rows:string[]=[];if(value.narrator)rows.push(`Рассказчик — ${label(value.narrator)}`);const characters=value.characters&&typeof value.characters==='object'?value.characters as Record<string,unknown>:{};const names=value.characterLabels&&typeof value.characterLabels==='object'?value.characterLabels as Record<string,string>:{};const entries=Object.keys(names).length?Object.entries(names).map(([id,name])=>[name,characters[id]] as const):Object.entries(characters).map(([name,voice],index)=>[/^[0-9a-f-]{36}$/i.test(name)?`Персонаж ${index+1}`:name,voice] as const);for(const [name,voice] of entries)rows.push(`${name} — ${label(voice)}`);return rows.join('; ');
}
function Notice({ text }: { text: string }) {
  return <div className="notice">{text}</div>;
}
function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
}) {
  return (
    <AppDialog title={title} onClose={close}>
      {children}
    </AppDialog>
  );
}
const minimumPrice = (model: Model) => {
  if (model.pricing.kind === "per_utf8_byte") return Number(model.pricing.creditsPerMillionBytes??0)/1000000;
  if (model.pricing.kind === "fixed") return Number(model.pricing.credits || 0);
  if (model.pricing.kind === "fixed_by_duration")
    return Number(
      model.pricing.creditsByDuration?.[String(model.durationsSeconds[0])] || 0,
    );
  if (model.pricing.kind === "per_1000_characters")
    return Number(model.pricing.creditsPer1000 || 0);
  if (model.pricing.kind === "matrix") {
    const row = model.pricing.rows?.[0];
    return (
      Number(row?.credits ?? row?.creditsPerSecond ?? 0) *
      (row?.credits ? 1 : model.durationsSeconds[0] || 0)
    );
  }
  return (
    Number(model.pricing.creditsPerSecond || 0) *
    (model.durationsSeconds[0] || 0)
  );
};

type StudioProject = {
  id: string;
  title: string;
  topic: string;
  aspectRatio: "9:16" | "16:9" | "1:1";
  status: string;
  totalDurationMs: string;
  budgetUnits: string | null;
  spentUnits: string;
  finalMediaId: string | null;
  scenarioSourceMode?: ScenarioSourceMode | null;
  scenarioKickoffMode?: ScenarioKickoffMode | null;
  updatedAt: string;
};
type StudioNode = {
  node_key: "scenario" | "storyboard" | "animation" | "voice" | "montage";
  mode: "manual" | "auto";
  status: string;
  config: Record<string, unknown>;
  safe_error_message?: string;
};
type StudioScene = {
  id: string;
  position: number;
  title: string;
  description: string;
  duration_ms: string;
  status: string;
  scene_type?: "illustration" | "breather";
  image_media_id?: string;
  animation_media_id?: string;
  voice_media_id?: string;
  narration?: string;
  dialogue?: Array<{ speaker: string; text: string }>;
  shot_plan?: {kind:string;focus?:string;composition?:string};
  production_overrides?: Record<string,{modelId?:string;outputResolution?:string;generateAudio?:boolean;parameters?:Record<string,unknown>}>;
};
type StudioRenderJob = {
  id: string;
  status: string;
  renderer: string;
  warnings?: string[];
  safe_error_message?: string;
  render_evidence?: Record<string, unknown>;
};
type StudioDetail = {
  project: StudioProject;
  nodes: StudioNode[];
  scenarioVersions: Array<
    ScenarioVersion & { conversation?: Array<{ role: string; text: string }> }
  >;
  scenes: StudioScene[];
  scenarioModels: ScenarioTextModel[];
  scenarioMessages?: ScenarioMessage[];
  scenarioProposals?: ScenarioProposal[];
  cast?: ProjectCharacter[];
  sceneCast?: SceneCharacterAssignment[];
  renderJobs?: StudioRenderJob[];
  scenarioOperation?: ScenarioOperation | null;
};
type SceneModelCompatibility={node:"storyboard"|"animation"|"voice";sceneModelIds:Record<string,string[]>;defaultModelIds:string[]};
type ScenarioOperation={id:string;status:'queued'|'running'|'completed'|'needs_attention'|'failed'|'reconciling';phase:'queued'|'drafting'|'checking'|'repairing'|'ready'|'stopped';attempt?:number;maxAttempts?:number;safeMessage?:string|null;result?:{assemblyUpdated?:boolean;kickoffReady?:boolean}|null};
type ExecutionQuote = {
  quoteId: string;
  fingerprint: string;
  expiresAt: string;
  steps: Array<{
    node: string;
    modelId: string;
    jobs: Array<{
      sceneId: string;
      modelId?: string;
      position: number;
      durationSeconds: number | null;
      inputMode: string;
      outputResolution?: string | null;
      generateAudio?: boolean;
      estimatedUnits: string;
      references: Array<{ id: string; role?: string }>;
      parameters: Record<string, unknown>;
      voiceAssignment?: Record<string, unknown> | null;
    }>;
    totalUnits: string;
  }>;
  excluded: Array<{ sceneId: string; reason: string }>;
  retainedSelections?: Array<{
    sceneId: string;
    position: number;
    title: string;
    node: string;
    mediaId: string;
    freshness: string;
  }>;
  totals: { units: string; credits: string };
  stopBoundary: string;
  blockers: string[];
  warnings: string[];
};
type ExecutionRun = {
  id: string;
  state: string;
  checkpoint: number;
  stopReason?: string | null;
  plan?: Record<string, unknown>;
};
type Variant = {
  jobId: string;
  mediaId?: string | null;
  status: string;
  errorMessage?: string | null;
  selected: boolean;
  compatible: boolean;
  kind?: string;
  createdAt: string;
};
type VariantResult = {
  selection: {
    jobId: string;
    mediaId: string;
    selectionVersion: number;
    freshness: string;
  } | null;
  variants: Variant[];
};
const studioNodes = [
  { key: "scenario", label: "Сценарий", icon: MessageSquare, kind: null },
  { key: "storyboard", label: "Кадры", icon: Images, kind: "image" },
  { key: "animation", label: "Анимация", icon: Film, kind: "video" },
  { key: "voice", label: "Озвучка", icon: Mic2, kind: "audio" },
  { key: "montage", label: "Монтаж", icon: Clapperboard, kind: null },
] as const;
const shotPlanLabels:Record<string,string>={establishing:"Общий план",wide:"Широкий план",medium:"Средний план",close_up:"Крупный план",detail:"Деталь",pov:"От первого лица",insert:"Врезка"};
const studioStatus: Record<string, string> = {
  draft: "Черновик",
  scenario: "Сценарий",
  storyboard: "Кадры",
  animation: "Анимация",
  voice: "Озвучка",
  montage: "Монтаж",
  completed: "Готово",
  pending: "Ожидает",
  waiting: "Ожидает",
  ready: "Готов к запуску",
  running: "В работе",
  failed: "Ошибка",
  idle: "Ожидает",
};
const selectedLookName = (
  character: ProjectCharacter | undefined,
  lookId: string | null,
) => {
  if (!character) return "";
  if (!lookId) return "базовый";
  const look = [
    ...character.characterSnapshot.looks,
    ...character.projectLooks,
  ].find((item) => String(item.id) === lookId);
  return String(look?.name ?? "недоступный образ");
};

export function StudioWorkspace({
  models,
  media,
  userId,
  onChanged,
}: {
  models: Model[];
  media: MediaAsset[];
  userId: string;
  onChanged: () => void;
}) {
  const initialHistory =
    typeof window === "undefined"
      ? null
      : (history.state as {
          vgenStudioProject?: unknown;
          vgenStudioNode?: unknown;
        } | null);
  const historyProject =
    typeof initialHistory?.vgenStudioProject === "string"
      ? initialHistory.vgenStudioProject
      : null;
  const historyNode = studioNodes.some(
    (item) => item.key === initialHistory?.vgenStudioNode,
  )
    ? (initialHistory?.vgenStudioNode as StudioNode["node_key"])
    : null;
  const [projects, setProjects] = useState<StudioProject[]>([]);
  const [scenarioTemplates, setScenarioTemplates] = useState<
    ScenarioTemplateChoice[]
  >([]);
  const [selected, setSelected] = useState<string | null>(historyProject);
  const selectedRef = useRef<string | null>(historyProject);
  const [detail, setDetail] = useState<StudioDetail | null>(null);
  const [modelCompatibility,setModelCompatibility]=useState<SceneModelCompatibility|null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [ratio, setRatio] = useState<"9:16" | "16:9" | "1:1">("9:16");
  const [active, setActive] = useState<StudioNode["node_key"]>(
    historyNode ?? "scenario",
  );
  const [focusedScene, setFocusedScene] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [nodeModel, setNodeModel] = useState<Record<string, string>>({});
  const pendingNodeSettings = useRef<
    Record<
      string,
      {
        revision: number;
        config: Record<string, unknown>;
        mode: "manual" | "auto";
        acknowledged: boolean;
        error: boolean;
      }
    >
  >({});
  const nodeWriteQueue = useRef<Record<string, Promise<void>>>({});
  const settingsRevision = useRef<Record<string, number>>({});
  const mutationEpoch = useRef<Record<string, number>>({});
  const detailRequest = useRef<Record<string, number>>({});
  const [narratorVoice, setNarratorVoice] = useState(FISH_DEFAULT_VOICE);
  const [characterVoices, setCharacterVoices] = useState<
    Record<string, string>
  >({});
  const [templateOpen, setTemplateOpen] = useState(false);
  const [deleteTarget,setDeleteTarget]=useState<StudioProject|null>(null);
  const [templateName, setTemplateName] = useState("");
  const [editingScene, setEditingScene] = useState<string | null>(null);
  const [inspectedScene, setInspectedScene] = useState<StudioScene | null>(
    null,
  );
  const inspectedSceneRef = useRef<string | null>(null);
  const [quote, setQuote] = useState<ExecutionQuote | null>(null);
  const [quoteScope, setQuoteScope] = useState<{
    scope: "batch" | "scene" | "failed";
    sceneId?: string;
  } | null>(null);
  const [currentRun, setCurrentRun] = useState<ExecutionRun | null>(null);
  const [variants, setVariants] = useState<VariantResult | null>(null);
  const [variantsBusy, setVariantsBusy] = useState(false);
  const [startUncertain, setStartUncertain] = useState(false);
  const [scenarioQuote,setScenarioQuote]=useState<{maxCredits:string;maxAttempts:number}|null>(null);
  const createRequestRef=useRef<{fingerprint:string;idempotencyKey:string}|null>(null);
  const activeRef = useRef(active);
  const quoteRef = useRef<ExecutionQuote | null>(null);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  useEffect(() => {
    quoteRef.current = quote;
  }, [quote]);
  useEffect(() => {
    inspectedSceneRef.current = inspectedScene?.id ?? null;
  }, [inspectedScene?.id]);
  const settingsKey = (projectId: string, key: StudioNode["node_key"]) =>
    `${projectId}:${key}`;
  const loadProjects = async () => {
    const projectResult = await api<{ projects: StudioProject[] }>(
      "/api/studio/projects",
    );
    setProjects(projectResult.projects);
    try {
      const scenarioTemplateResult = await api<{
        templates: ScenarioTemplateChoice[];
      }>("/api/scenario-templates?scope=shared");
      setScenarioTemplates(
        (scenarioTemplateResult?.templates ?? []).filter(
          (template) =>
            !template.publicationState ||
            template.publicationState === "published",
        ),
      );
    } catch {
      // A temporary library failure must not hide the user's Studio projects.
      setScenarioTemplates([]);
    }
  };
  const loadDetail = async (id = selectedRef.current) => {
    if (!id) return;
    const request = (detailRequest.current[id] ?? 0) + 1;
    detailRequest.current[id] = request;
    const epoch = mutationEpoch.current[id] ?? 0;
    const result = await api<StudioDetail>(`/api/studio/projects/${id}`);
    if (
      detailRequest.current[id] !== request ||
      selectedRef.current !== id ||
      epoch !== (mutationEpoch.current[id] ?? 0)
    )
      return;
    const effectiveNodes = (result.nodes ?? []).map((node) => {
      const key = settingsKey(id, node.node_key);
      const desired = pendingNodeSettings.current[key];
      if (!desired) return node;
      const confirmed =
        desired.acknowledged &&
        node.mode === desired.mode &&
        Object.entries(desired.config).every(
          ([field, value]) =>
            JSON.stringify(node.config[field]) === JSON.stringify(value),
        );
      if (confirmed) {
        delete pendingNodeSettings.current[key];
        return node;
      }
      return {
        ...node,
        mode: desired.mode,
        config: { ...node.config, ...desired.config },
      };
    });
    const effective = { ...result, nodes: effectiveNodes };
    setDetail(effective);
    const nextModels: Record<string, string> = {};
    for (const node of effectiveNodes) {
      const kind = studioNodes.find((item) => item.key === node.node_key)?.kind;
      const options = models.filter(
        (model) =>
          model.generationKind === kind &&
          (node.node_key !== "voice" || model.family !== "Suno"),
      );
      const configured = options.find(
        (model) => model.id === node.config.modelId,
      );
      nextModels[node.node_key] = configured?.id ?? options[0]?.id ?? "";
    }
    setNodeModel(nextModels);
    const voiceNode = effectiveNodes.find((item) => item.node_key === "voice");
    const voiceModel = models.find((item) => item.id === nextModels.voice);
    const voiceField = voiceModel?.uiSchema?.advanced?.find((field) =>
      field.id.toLowerCase().includes("voice"),
    );
    const defaultVoice = String(
      voiceField?.defaultValue ?? voiceField?.options?.[0]?.value ?? FISH_DEFAULT_VOICE,
    );
    const configuredNarrator = voiceNode?.config.narratorVoice as
      { voiceId?: unknown } | undefined;
    setNarratorVoice(voiceField?.options?.some(v=>v.value===configuredNarrator?.voiceId)?String(configuredNarrator?.voiceId):defaultVoice);
    const configuredCharacters = voiceNode?.config.characterVoices;
    setCharacterVoices(
      configuredCharacters && typeof configuredCharacters === "object"
        ? Object.fromEntries(
            Object.entries(configuredCharacters).map(([key, value]) => [
              key,
              String(
                (value as Record<string, unknown>)?.voiceId ?? defaultVoice,
              ),
            ]),
          )
        : {},
    );
  };
  useEffect(() => {
    void loadProjects();
  }, []);
  useEffect(() => {
    selectedRef.current = selected;
    setDetail(null);
    setModelCompatibility(null);
    setNodeModel({});
    setNarratorVoice(FISH_DEFAULT_VOICE);
    setCharacterVoices({});
    setInspectedScene(null);
    setQuote(null);
    setVariants(null);
    setCurrentRun(null);
    setStartUncertain(false);
    if (!selected) return;
    void loadDetail(selected);
    const timer = setInterval(() => void loadDetail(selected), 5000);
    return () => clearInterval(timer);
  }, [selected, models.length]);
  const compatibilityKey=detail?JSON.stringify({scenes:detail.scenes.map((scene)=>({id:scene.id,status:scene.status,sceneType:scene.scene_type,image:scene.image_media_id,animation:scene.animation_media_id,voice:scene.voice_media_id,narration:scene.narration,dialogue:scene.dialogue,overrides:scene.production_overrides})),cast:detail.sceneCast??[]}):"";
  useEffect(() => {
    const paidNode=active==="storyboard"||active==="animation"||active==="voice"?active:null;if(!detail||!paidNode){setModelCompatibility(null);return;}
    let live=true;setModelCompatibility(null);
    void api<SceneModelCompatibility>(`/api/studio/projects/${detail.project.id}/production/${paidNode}/compatibility`).then((result)=>{if(live)setModelCompatibility(result)}).catch(()=>{if(live){setModelCompatibility({node:paidNode,sceneModelIds:{},defaultModelIds:[]});setMessage("Не удалось проверить совместимость моделей. Обновите проект.")}});
    return()=>{live=false};
  },[detail?.project.id,active,compatibilityKey]);

  useEffect(() => {
    if (!selected) return;
    let stored: {
      id?: string;
      key?: string;
      quoteId?: string;
      state?: string;
    } | null = null;
    try {
      stored = JSON.parse(
        localStorage.getItem(`vgen.studio-start:${userId}:${selected}`) ||
          "null",
      ) as {
        id?: string;
        key?: string;
        quoteId?: string;
        state?: string;
      } | null;
    } catch {
      /* invalid recovery record */
    }
    if (!stored?.id && !stored?.key) return;
    let activeRequest = true;
    if (!stored.id) setStartUncertain(true);
    const refresh = () =>
      api<{ run: ExecutionRun }>(
        stored!.id
          ? `/api/studio/projects/${selected}/executions/${stored!.id}`
          : `/api/studio/projects/${selected}/executions/by-start-key/${encodeURIComponent(stored!.key!)}`,
      )
        .then((result) => {
          if (!activeRequest || selectedRef.current !== selected) return;
          setCurrentRun(result.run);
          setStartUncertain(false);
          localStorage.setItem(
            `vgen.studio-start:${userId}:${selected}`,
            JSON.stringify({
              ...stored,
              id: result.run.id,
              state: result.run.state,
            }),
          );
          if (["completed", "failed", "cancelled"].includes(result.run.state))
            void loadDetail(selected);
        })
        .catch(() => {
          if (activeRequest && selectedRef.current === selected && stored?.key)
            setStartUncertain(true);
        });
    void refresh();
    const timer = setInterval(() => void refresh(), 5000);
    return () => {
      activeRequest = false;
      clearInterval(timer);
    };
  }, [selected, userId]);
  useEffect(() => {
    if (
      !inspectedScene ||
      !detail ||
      !["storyboard", "animation", "voice"].includes(active)
    ) {
      setVariants(null);
      return;
    }
    let live = true;
    setVariantsBusy(true);
    void api<VariantResult>(
      `/api/studio/projects/${detail.project.id}/scenes/${inspectedScene.id}/variants/${active}`,
    )
      .then((result) => {
        if (live) setVariants(Array.isArray(result.variants) ? result : null);
      })
      .catch((error) => {
        if (live) setMessage((error as Error).message);
      })
      .finally(() => {
        if (live) setVariantsBusy(false);
      });
    return () => {
      live = false;
    };
  }, [inspectedScene?.id, active, detail?.project.id]);
  useEffect(() => {
    const pop = () => {
      if (selected && history.state?.vgenStudioProject === selected) {
        setTemplateOpen(false);
        setEditingScene(null);
        return;
      }
      if (templateOpen) {
        setTemplateOpen(false);
        return;
      }
      if (editingScene) {
        setEditingScene(null);
        return;
      }
      if (createOpen) {
        setCreateOpen(false);
        return;
      }
      if (selected) {
        setSelected(null);
        setDetail(null);
    setModelCompatibility(null);
      }
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [selected, createOpen, templateOpen, editingScene]);
  const navigateProject = (
    id: string,
    stage: StudioNode["node_key"],
    replace = false,
  ) => {
    const state = {
      ...(history.state ?? {}),
      vgenStudioProject: id,
      vgenStudioNode: stage,
    };
    if (replace) history.replaceState(state, "");
    else history.pushState(state, "");
    try {
      localStorage.setItem(`vgen.last-studio-project:${userId}`, id);
      localStorage.setItem(`vgen.last-studio-stage:${userId}:${id}`, stage);
    } catch {
      /* browser history is enough */
    }
    selectedRef.current = id;
    setActive(stage);
    setSelected(id);
  };
  const selectStage = (stage: StudioNode["node_key"]) => {
    const id = selectedRef.current;
    if (!id) return;
    try {
      localStorage.setItem(`vgen.last-studio-stage:${userId}:${id}`, stage);
    } catch {
      /* history remains usable */
    }
    history.replaceState(
      {
        ...(history.state ?? {}),
        vgenStudioProject: id,
        vgenStudioNode: stage,
      },
      "",
    );
    setActive(stage);
  };
  const openProject = (id: string) => {
    const stored = localStorage.getItem(
      `vgen.last-studio-stage:${userId}:${id}`,
    ) as StudioNode["node_key"] | null;
    const stage: StudioNode["node_key"] =
      historyNode ??
      (studioNodes.some((item) => item.key === stored)
        ? (stored as StudioNode["node_key"])
        : "scenario");
    navigateProject(id, stage);
  };
  useEffect(() => {
    if (!selected || !detail || active === "scenario") return;
    const key = `vgen.studio-scroll:${userId}:${selected}:${active}`;
    const scroller = document.querySelector<HTMLElement>(".app-scroll");
    if (!scroller) return;
    const restore = window.requestAnimationFrame(() => {
      scroller.scrollTop = Number(sessionStorage.getItem(key) ?? 0);
    });
    const save = () => sessionStorage.setItem(key, String(scroller.scrollTop));
    scroller.addEventListener("scroll", save, { passive: true });
    return () => {
      window.cancelAnimationFrame(restore);
      save();
      scroller.removeEventListener("scroll", save);
    };
  }, [selected, active, userId, detail?.project.id]);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMessage("");
    try {
      await fn();
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const persistNodeSettings = async (
    key: StudioNode["node_key"],
    config: Record<string, unknown>,
    mode?: "manual" | "auto",
  ) => {
    if (!detail) return;
    const projectId = detail.project.id;
    const pendingKey = settingsKey(projectId, key);
    const target = detail.nodes.find((item) => item.node_key === key);
    const previous = pendingNodeSettings.current[pendingKey];
    const snapshot = {
      ...(previous?.config ?? target?.config ?? {}),
      ...config,
    };
    const desiredMode = mode ?? previous?.mode ?? target?.mode ?? "manual";
    const revision = (settingsRevision.current[pendingKey] ?? 0) + 1;
    settingsRevision.current[pendingKey] = revision;
    mutationEpoch.current[projectId] =
      (mutationEpoch.current[projectId] ?? 0) + 1;
    pendingNodeSettings.current[pendingKey] = {
      revision,
      config: snapshot,
      mode: desiredMode,
      acknowledged: false,
      error: false,
    };
    setDetail((current) =>
      current?.project.id === projectId
        ? {
            ...current,
            nodes: current.nodes.map((node) =>
              node.node_key === key
                ? { ...node, mode: desiredMode, config: snapshot }
                : node,
            ),
          }
        : current,
    );
    const queued = nodeWriteQueue.current[pendingKey] ?? Promise.resolve();
    const write = queued
      .catch(() => {})
      .then(async () => {
        try {
          await api(`/api/studio/projects/${projectId}/nodes/${key}`, {
            method: "PATCH",
            body: JSON.stringify({ mode: desiredMode, config: snapshot }),
          });
          mutationEpoch.current[projectId] =
            (mutationEpoch.current[projectId] ?? 0) + 1;
          const desired = pendingNodeSettings.current[pendingKey];
          if (desired?.revision === revision) {
            desired.acknowledged = true;
            await loadDetail(projectId);
          }
        } catch (error) {
          const desired = pendingNodeSettings.current[pendingKey];
          if (desired?.revision === revision) desired.error = true;
          if (selectedRef.current === projectId)
            setMessage(
              `Настройки не сохранены. Повторите выбор перед запуском. ${(error as Error).message}`,
            );
          throw error;
        }
      });
    nodeWriteQueue.current[pendingKey] = write;
    await write;
  };
  const node = detail?.nodes.find((item) => item.node_key === active);
  const nodeDefinition = studioNodes.find((item) => item.key === active)!;
  const nodeModels = models.filter(
    (model) =>
      model.generationKind === nodeDefinition.kind &&
      (active !== "voice" || model.family !== "Suno"),
  );
  const selectedModel =
    nodeModels.find((model) => model.id === nodeModel[active]) ?? nodeModels[0];
  const musicAssets = media.filter(
    (item) =>
      !item.temporary &&
      ["source_audio", "generated_audio"].includes(item.kind),
  );
  const configFor = (
    key: string,
    options: {
      modelId?: string;
      narratorVoice?: string;
      characterVoices?: Record<string, string>;
    } = {},
  ) => {
    const model = models.find(
      (item) => item.id === (options.modelId ?? nodeModel[key]),
    );
    if (!model) return {};
    const base = {
      modelId: model.id,
      outputResolution:
        model.outputResolutions?.[0] ??
        (model.generationKind === "audio" ? "audio" : "480p"),
      parameters: Object.fromEntries(
        model.uiSchema?.advanced?.map((field) => [
          field.id,
          field.defaultValue,
        ]) ?? [],
      ),
    };
    const selectedNarrator = options.narratorVoice ?? narratorVoice;
    const selectedCharacters = options.characterVoices ?? characterVoices;
    return key === "voice"
      ? {
          ...base,
          narratorVoice: { voiceId: selectedNarrator },
          characterVoices: Object.fromEntries(
            Object.entries(selectedCharacters).map(([id, voiceId]) => [
              id,
              { voiceId },
            ]),
          ),
        }
      : base;
  };
  const chooseNodeModel = (key: StudioNode["node_key"], modelId: string) => {
    setNodeModel((current) => ({ ...current, [key]: modelId }));
    void persistNodeSettings(key, configFor(key, { modelId })).catch(
      () => undefined,
    );
  };
  const sceneModelOptions = (scene: StudioScene) => !modelCompatibility||modelCompatibility.node!==active ? [] : nodeModels.filter((candidate)=>(modelCompatibility.sceneModelIds[scene.id]??[]).includes(candidate.id));
  const defaultModelOptions = modelCompatibility?.node===active ? nodeModels.filter((candidate)=>modelCompatibility.defaultModelIds.includes(candidate.id)) : [];
  const chooseSceneModel = (scene: StudioScene, modelId: string) => act(async () => {
    if (!detail || !["storyboard","animation","voice"].includes(active)) return;
    const mode = modelId ? "override" : "inherit";
    const model = models.find((item) => item.id === modelId);
    await api(`/api/studio/projects/${detail.project.id}/scenes/${scene.id}/production/${active}`,{method:"PATCH",body:JSON.stringify({mode,...(mode === "override" && model ? {config:configFor(active,{modelId:model.id})} : {})})});
    mutationEpoch.current[detail.project.id]=(mutationEpoch.current[detail.project.id]??0)+1;await loadDetail(detail.project.id);
  });
  const applyDefaultModelToAll = () => act(async () => {
    if (!detail || !selectedModel || !["storyboard","animation","voice"].includes(active)) return;
    await api(`/api/studio/projects/${detail.project.id}/production/${active}/default`,{method:"PUT",body:JSON.stringify({config:configFor(active,{modelId:selectedModel.id})})});
    mutationEpoch.current[detail.project.id]=(mutationEpoch.current[detail.project.id]??0)+1;await loadDetail(detail.project.id);
  });
  const setMode = (mode: "manual" | "auto") =>
    act(async () => {
      if (!detail || !node) return;
      await persistNodeSettings(active, configFor(active), mode);
    });
  const runNode = (sceneId?: string) =>
    act(async () => {
      if (!detail) return;
      const projectId = detail.project.id;
      const stage = active;
      const key = settingsKey(projectId, stage);
      const revision = settingsRevision.current[key] ?? 0;
      await (nodeWriteQueue.current[key] ?? Promise.resolve());
      if (selectedRef.current !== projectId) return;
      if ((settingsRevision.current[key] ?? 0) !== revision) {
        setMessage(
          "Настройки изменились во время ожидания. Проверьте их и запустите этап ещё раз.",
        );
        return;
      }
      if (pendingNodeSettings.current[key]?.error)
        throw new Error("Сначала сохраните настройки этапа");
      const scope = sceneId
        ? "scene"
        : node?.status === "failed"
          ? "failed"
          : "batch";
      const stages = ["storyboard", "animation", "voice", "montage"] as const;
      const index = stages.indexOf(stage as (typeof stages)[number]);
      let chainEnd: (typeof stages)[number] | undefined;
      if (index >= 0) {
        let cursor = index + 1;
        while (
          cursor < stages.length &&
          detail.nodes.find((item) => item.node_key === stages[cursor])
            ?.mode === "auto"
        ) {
          chainEnd = stages[cursor];
          cursor++;
        }
      }
      const payload = {
        scope,
        node: stage,
        ...(sceneId ? { sceneId } : {}),
        ...(chainEnd ? { chainEnd } : {}),
      };
      const result = await api<ExecutionQuote>(
        `/api/studio/projects/${projectId}/executions/preflight`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      if (
        selectedRef.current !== projectId ||
        activeRef.current !== stage ||
        (settingsRevision.current[key] ?? 0) !== revision
      )
        return;
      if (!Array.isArray(result.steps) || !Array.isArray(result.blockers))
        throw new Error(
          "Сервер не вернул проверку запуска. Обновите проект и повторите.",
        );
      setQuoteScope({ scope, ...(sceneId ? { sceneId } : {}) });
      setQuote(result);
    });
  const recoverStart = async (
    projectId: string,
    key: string,
    quoteId?: string,
    fingerprint?: string,
  ) => {
    const result = await api<{ run: ExecutionRun }>(
      `/api/studio/projects/${projectId}/executions/by-start-key/${encodeURIComponent(key)}`,
    );
    if (selectedRef.current !== projectId) return;
    setCurrentRun(result.run);
    localStorage.setItem(
      `vgen.studio-start:${userId}:${projectId}`,
      JSON.stringify({
        id: result.run.id,
        key,
        quoteId,
        fingerprint: fingerprint ?? quoteRef.current?.fingerprint,
        state: result.run.state,
      }),
    );
    setQuote(null);
    setMessage("Запуск принят. Статус восстановлен после потери связи.");
    await loadDetail(projectId);
  };
  const confirmStart = () =>
    act(async () => {
      if (!detail || !quote) return;
      const projectId = detail.project.id,
        storageKey = `vgen.studio-start:${userId}:${projectId}`;
      const quoteId = quote.quoteId;
      let saved: {
        key?: string;
        fingerprint?: string;
        quoteId?: string;
        state?: string;
      } | null = null;
      try {
        saved = JSON.parse(localStorage.getItem(storageKey) || "null") as {
          key?: string;
          fingerprint?: string;
          quoteId?: string;
          state?: string;
        } | null;
      } catch {
        /* create a new stable key */
      }
      const key =
        saved?.quoteId === quoteId && saved.state === "pending" && saved.key
          ? saved.key
          : crypto.randomUUID();
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          key,
          quoteId,
          fingerprint: quote.fingerprint,
          state: "pending",
        }),
      );
      try {
        const result = await api<{ run: ExecutionRun }>(
          `/api/studio/projects/${projectId}/executions/start`,
          {
            method: "POST",
            headers: { "idempotency-key": key },
            body: JSON.stringify({
              quoteId: quote.quoteId,
              fingerprint: quote.fingerprint,
            }),
          },
        );
        if (
          selectedRef.current !== projectId ||
          quoteRef.current?.quoteId !== quoteId
        )
          return;
        setCurrentRun(result.run);
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            id: result.run.id,
            key,
            quoteId,
            fingerprint: quote.fingerprint,
            state: result.run.state,
          }),
        );
        setQuote(null);
        setMessage("Запуск принят");
        mutationEpoch.current[projectId] =
          (mutationEpoch.current[projectId] ?? 0) + 1;
        await loadDetail(projectId);
      } catch (error) {
        if (
          selectedRef.current !== projectId ||
          quoteRef.current?.quoteId !== quoteId
        )
          return;
        setStartUncertain(true);
        try {
          await recoverStart(projectId, key, quoteId, quote.fingerprint);
        } catch {
          throw new Error(
            `Не удалось подтвердить запуск: ${(error as Error).message}. Новая отправка не выполнялась — нажмите «Проверить статус».`,
          );
        }
      }
    });
  const checkUncertainStart = () =>
    act(async () => {
      if (!detail) return;
      const stored = JSON.parse(
        localStorage.getItem(
          `vgen.studio-start:${userId}:${detail.project.id}`,
        ) || "null",
      ) as { key?: string; quoteId?: string; fingerprint?: string };
      if (!stored?.key) throw new Error("Ключ запуска не найден");
      await recoverStart(detail.project.id, stored.key, stored.quoteId, stored.fingerprint);
    });
  const stopRun = () =>
    act(async () => {
      if (!detail || !currentRun) return;
      const result = await api<{ run: ExecutionRun }>(
        `/api/studio/projects/${detail.project.id}/executions/${currentRun.id}/stop`,
        { method: "POST" },
      );
      if (selectedRef.current !== detail.project.id) return;
      setCurrentRun(result.run);
      setMessage(
        "Новые отправки остановлены. Уже принятые провайдером задачи сохранят результат.",
      );
      await loadDetail(detail.project.id);
    });
  const selectVariant = (variant: Variant) =>
    act(async () => {
      if (!detail || !inspectedScene || !variants || !variant.mediaId) return;
      const projectId = detail.project.id;
      const sceneId = inspectedScene.id;
      const stage = active;
      const result = await api<{ selection: VariantResult["selection"] }>(
        `/api/studio/projects/${projectId}/scenes/${sceneId}/variants/${stage}/select`,
        {
          method: "POST",
          body: JSON.stringify({
            jobId: variant.jobId,
            mediaId: variant.mediaId,
            expectedVersion: variants.selection?.selectionVersion ?? 0,
          }),
        },
      );
      if (
        selectedRef.current !== projectId ||
        activeRef.current !== stage ||
        inspectedSceneRef.current !== sceneId
      )
        return;
      setVariants((current) =>
        current
          ? {
              ...current,
              selection: result.selection,
              variants: current.variants.map((item) => ({
                ...item,
                selected:
                  item.jobId === variant.jobId &&
                  item.mediaId === variant.mediaId,
              })),
            }
          : current,
      );
      setMessage("Вариант выбран. Предыдущие результаты сохранены.");
      await loadDetail(projectId);
    });
  const createWithScenarioSource = (
    mode: ScenarioSourceMode,
    templateId?: string,
  ) =>
    act(async () => {
      const payload={aspectRatio:ratio,source:{mode,...(templateId?{templateId}:{})}};
      const storageKey=`vgen.studio-create:${userId}`;
      const fingerprint=JSON.stringify(payload);
      let stored=createRequestRef.current;
      if(!stored){try{stored=JSON.parse(localStorage.getItem(storageKey)??'null') as typeof stored}catch{/* create remains available in this window */}}
      if(!stored||stored.fingerprint!==fingerprint)stored={fingerprint,idempotencyKey:crypto.randomUUID()};
      createRequestRef.current=stored;
      try{localStorage.setItem(storageKey,JSON.stringify(stored))}catch{/* in-memory key still protects this window */}
      const result = await api<{ project: StudioProject }>(
        "/api/studio/projects/start",
        { method: "POST", body: JSON.stringify({...payload,idempotencyKey:stored.idempotencyKey}) },
      );
      createRequestRef.current=null;
      try{localStorage.removeItem(storageKey)}catch{/* successful response is authoritative */}
      setProjects((current) => [
        { ...result.project, scenarioSourceMode: mode },
        ...current.filter((project) => project.id !== result.project.id),
      ]);
      setCreateOpen(false);
      navigateProject(result.project.id, "scenario", true);
      setMessage("Способ сценария выбран");
      void loadProjects().catch(() => {
        if (selectedRef.current === result.project.id)
          setMessage("Проект создан. Список проектов обновится позже.");
      });
    });
  const chooseScenarioSource = (
    mode: ScenarioSourceMode,
    templateId?: string,
  ) =>
    act(async () => {
      if (!detail) return;
      const projectId = detail.project.id;
      await api(`/api/studio/projects/${projectId}/scenario/source`, {
        method: "POST",
        body: JSON.stringify({ mode, ...(templateId ? { templateId } : {}) }),
      });
      await loadDetail(projectId);
    });
  const createInitialScenario = async (projectId: string, continuation = false) => {
    const storageKey = `vgen.scenario-initial:${userId}:${projectId}`;
    let idempotencyKey = localStorage.getItem(storageKey);
    if (!idempotencyKey) {
      idempotencyKey = crypto.randomUUID();
      localStorage.setItem(storageKey, idempotencyKey);
    }
    try {
      await api(`/api/studio/projects/${projectId}/scenario/develop`, {
        method: "POST",
        body: JSON.stringify({
          initialAuto: true,
          ...(continuation?{continuation:true}:{}),
          modelId: scenarioModelId,
          ...(!continuation?{maxCredits:scenarioQuote?.maxCredits}:{}),
          message: continuation
            ? "Дострой уже начатый вариант из диалога и сохрани полный редактируемый сценарий. Не начинай историю заново."
            : "Подготовь полный первый вариант сценария по выбранному способу и данным проекта. Самостоятельно выбери подходящих персонажей из общей библиотеки.",
          idempotencyKey,
        }),
      });
      localStorage.removeItem(storageKey);
    } catch (error) {
      const ambiguous = (error as { body?: { ambiguous?: unknown } }).body
        ?.ambiguous;
      if (ambiguous === false) localStorage.removeItem(storageKey);
      throw error;
    }
  };
  const chooseScenarioKickoff = (mode: ScenarioKickoffMode) =>
    act(async () => {
      if (!detail) return;
      const projectId = detail.project.id;
      await api(`/api/studio/projects/${projectId}/scenario/kickoff`, {
        method: "POST",
        body: JSON.stringify({ mode,...(mode==='auto'?{modelId:scenarioModelId,maxCredits:scenarioQuote?.maxCredits}: {}) }),
      });
      await loadDetail(projectId);
    });
  const retryInitialScenario = () =>
    act(async () => {
      if (!detail) return;
      await createInitialScenario(detail.project.id, detail.scenarioOperation?.attempt!==0);
      await loadDetail(detail.project.id);
    });
  const forkProject = () =>
    act(async () => {
      if (!detail) return;
      const result = await api<{ project: StudioProject }>(
        `/api/studio/projects/${detail.project.id}/fork`,
        {
          method: "POST",
          body: JSON.stringify({
            title: `${detail.project.title} — новая версия`,
          }),
        },
      );
      await loadProjects();
      navigateProject(result.project.id, "scenario", true);
      setMessage("Создана новая редактируемая версия проекта");
    });
  const copyProjectById = (project: StudioProject) =>
    act(async () => {
      const result=await api<{project:StudioProject}>(`/api/studio/projects/${project.id}/fork`,{method:'POST',body:JSON.stringify({title:`${project.title} — копия`})});
      await loadProjects();
      setMessage('Копия проекта создана');
      if(selectedRef.current===project.id)navigateProject(result.project.id,'scenario',true);
    });
  const deleteProject = (project: StudioProject) =>
    act(async () => {
      await api(`/api/studio/projects/${project.id}`,{method:'DELETE'});
      try{localStorage.removeItem(`vgen.last-studio-stage:${userId}:${project.id}`);if(localStorage.getItem(`vgen.last-studio-project:${userId}`)===project.id)localStorage.removeItem(`vgen.last-studio-project:${userId}`)}catch{/* list state remains valid */}
      setDeleteTarget(null);
      if(selectedRef.current===project.id){selectedRef.current=null;setSelected(null);setDetail(null);history.replaceState({...history.state,vgenStudioProject:undefined,vgenStudioNode:undefined},'')}
    setModelCompatibility(null);
      await loadProjects();
      setMessage('Проект удалён. Материалы библиотеки сохранены.');
    });
  const scenarioNode = detail?.nodes.find(
    (item) => item.node_key === "scenario",
  );
  const scenarioModels = detail?.scenarioModels ?? [];
  const scenarioModelId = String(
    scenarioNode?.config?.modelId ??
      scenarioModels.find((model) => model.id === "gemini-3-7-flash-openai")
        ?.id ??
      scenarioModels[0]?.id ??
      "",
  );
  useEffect(()=>{if(!detail?.project.scenarioSourceMode||detail.project.scenarioKickoffMode)return;let live=true;api<{maxCredits:string;maxAttempts:number}>(`/api/studio/projects/${detail.project.id}/scenario/quote?modelId=${encodeURIComponent(scenarioModelId)}&initialAuto=true`).then(value=>{if(live)setScenarioQuote(value)}).catch(()=>{if(live)setScenarioQuote(null)});return()=>{live=false}},[detail?.project.id,detail?.project.scenarioSourceMode,detail?.project.scenarioKickoffMode,scenarioModelId]);
  const chooseScenarioModel = (modelId: string) =>
    act(async () => {
      if (!detail || !scenarioNode) return;
      await persistNodeSettings("scenario", { modelId });
    });
  const lock = async (expectedVersionId?:string) => {
    if (!detail) return;
    const projectId = detail.project.id;
    setBusy(true);
    try {
      await api(`/api/studio/projects/${projectId}/scenario/lock`, {
        method: "POST",
        headers: {"x-scenario-version":expectedVersionId||"none"},
      });
      selectStage("storyboard");
      await loadDetail(projectId);
      await loadProjects();
    } finally {
      setBusy(false);
    }
  };
  const saveTemplate = () =>
    act(async () => {
      if (!detail) return;
      await api(`/api/studio/projects/${detail.project.id}/template`, {
        method: "POST",
        body: JSON.stringify({
          name: templateName,
          description: "Производственный конвейер мультфильма",
          keepAspectRatio: false,
        }),
      });
      history.back();
      setTemplateName("");
      setMessage("Шаблон сохранён");
    });
  const openSceneCast = (sceneId: string) => {
    if (!detail) return;
    history.pushState(
      {
        ...(history.state ?? {}),
        vgenStudioProject: detail.project.id,
        vgenStudioOverlay: "scene-cast",
      },
      "",
    );
    setEditingScene(sceneId);
  };
  const closeSceneCast = () => {
    if (history.state?.vgenStudioOverlay) history.back();
    else setEditingScene(null);
  };
  if (!selected) {
    return (
      <section className="page studio-page studio-workspace">
        <div className="title-row">
          <div>
            <h1>Мультстудия</h1>
          </div>
          <button
            className="primary compact"
            onClick={() => {
              history.pushState(
                { ...(history.state ?? {}), vgenStudioOverlay: "create" },
                "",
              );
              setCreateOpen(true);
            }}
          >
            <Plus />
            Новый проект
          </button>
        </div>
        {message && <Notice text={message} />}
        <div className="studio-projects">
          {projects.map((project) => (
            <ProjectListItem key={project.id} project={project} onOpen={()=>openProject(project.id)} onCopy={()=>void copyProjectById(project)} onDelete={()=>setDeleteTarget(project)}/>
          ))}
          {!projects.length && (
            <div className="studio-empty">
              <FolderKanban />
              <b>Начните первый мультфильм</b>
              <span>
                Тема превращается в сценарий, кадры, анимацию, озвучку и готовый
                ролик.
              </span>
            </div>
          )}
        </div>
        {deleteTarget&&<AppDialog title="Удалить проект?" onClose={()=>setDeleteTarget(null)}><p>Сценарий, настройки и история производства этого проекта будут удалены. Файлы из библиотеки останутся.</p><div className="dialog-actions"><button className="secondary" onClick={()=>setDeleteTarget(null)}>Отмена</button><button className="danger" disabled={busy} onClick={()=>void deleteProject(deleteTarget)}><Trash2/>Удалить проект</button></div></AppDialog>}
        {createOpen && (
          <Modal title="Новый мультфильм" close={() => history.back()}>
            <div className="studio-create-format">
              <span>Формат мультфильма</span>
              <div className="studio-ratios">
              {(["9:16", "16:9", "1:1"] as const).map((value) => (
                <button
                  type="button"
                  className={ratio === value ? "active" : ""}
                  key={value}
                  onClick={() => setRatio(value)}
                >
                  {value}
                </button>
              ))}
              </div>
            </div>
            <ScenarioOnboarding
              sourceMode={null}
              kickoffMode={null}
              templates={scenarioTemplates}
              busy={busy}
              onChooseSource={(mode, templateId) =>
                void createWithScenarioSource(mode, templateId)
              }
              onChooseKickoff={() => undefined}
            />
          </Modal>
        )}
      </section>
    );
  }
  if (!detail)
    return (
      <section className="page">
        <p className="muted">Загружаем проект…</p>
      </section>
    );
  const latest = detail.scenarioVersions[0];
  const scenarioMessages =
    detail.scenarioMessages ??
    (latest?.conversation ?? []).map((entry, index) => ({
      id: `legacy-${index}`,
      role: entry.role === "user" ? ("user" as const) : ("assistant" as const),
      content: entry.text,
      message_kind: "discussion",
    }));
  // Cached details from releases before the onboarding fields existed already
  // represent configured projects. New projects explicitly return null.
  const scenarioSourceMode =
    detail.project.scenarioSourceMode === undefined
      ? "freeform"
      : detail.project.scenarioSourceMode;
  const scenarioKickoffMode =
    detail.project.scenarioKickoffMode === undefined
      ? "discuss"
      : detail.project.scenarioKickoffMode;
  const scenarioOnboardingPending =
    !scenarioSourceMode || !scenarioKickoffMode;
  const eligibleScenes = detail.scenes.filter((scene) =>
    eligibleFor(active, scene),
  );
  const pendingScenes = missingFor(active, detail.scenes);
  const finishedScenes = eligibleScenes.length - pendingScenes.length;
  const nextAction = nextWork({
    scenes: detail.scenes,
    nodes: detail.nodes,
    locked: latest?.state === "locked",
    pendingProposals: (detail.scenarioProposals ?? []).filter(
      (item) => item.status === "pending",
    ).length,
    finalMediaId: detail.project.finalMediaId,
  });
  const spent = BigInt(detail.project.spentUnits);
  const limit =
    detail.project.budgetUnits === null
      ? null
      : BigInt(detail.project.budgetUnits);
  const settingsState = Object.entries(pendingNodeSettings.current)
    .filter(([key]) => key.startsWith(`${detail.project.id}:`))
    .map(([, value]) => value);
  const settingsText = settingsState.some((value) => value.error)
    ? "Ошибка сохранения настроек"
    : settingsState.some((value) => !value.acknowledged)
      ? "Сохраняем настройки…"
      : "Настройки сохранены";
  const regenerateLabel = (scene: StudioScene) =>
    `Перегенерировать ${active === "storyboard" ? "кадр" : active === "animation" ? "анимацию" : "озвучку"} сцены ${scene.position + 1}`;
  const canTarget = (scene: StudioScene) =>
    eligibleFor(active, scene) &&
    (active !== "animation" || Boolean(scene.image_media_id));
  const voiceOptions = [
    ...new Map(
      (selectedModel?.uiSchema?.advanced ?? [])
        .filter((field) => field.id.toLowerCase().includes("voice"))
        .flatMap((field) => field.options ?? [])
        .map((option) => [option.value, option]),
    ).values(),
  ];
  if (!voiceOptions.length)
    voiceOptions.push(
      ...FISH_VOICES.map(v=>({value:v.id,label:v.name,group:v.gender==='male'?'Мужские':'Женские',note:v.description})),
    );
  return (
    <section className="page studio-workspace">
      <div className="studio-project-head">
        <button aria-label="К проектам" onClick={() => history.back()}>
          <ArrowLeft />
        </button>
        <div>
          <h1>{detail.project.title}</h1>
          <small>
            {detail.project.aspectRatio} ·{" "}
            {Number(detail.project.totalDurationMs)>0?`${detail.project.finalMediaId?'':'≈ '}${Math.round(Number(detail.project.totalDurationMs)/1000)} сек. · `:''}
            {studioStatus[detail.project.status] ?? detail.project.status}
          </small>
        </div>
        <ProjectActionsMenu
          onTemplate={() => {
              history.pushState(
                { ...(history.state ?? {}), vgenStudioOverlay: "template" },
                "",
              );
              setTemplateOpen(true);
          }}
          onCopy={() => void forkProject()}
          onDelete={()=>setDeleteTarget(detail.project)}
        />
      </div>
      {message && <Notice text={message} />}
      {deleteTarget&&<AppDialog title="Удалить проект?" onClose={()=>setDeleteTarget(null)}><p>Сценарий, настройки и история производства этого проекта будут удалены. Файлы из библиотеки останутся.</p><div className="dialog-actions"><button className="secondary" onClick={()=>setDeleteTarget(null)}>Отмена</button><button className="danger" disabled={busy} onClick={()=>void deleteProject(deleteTarget)}><Trash2/>Удалить проект</button></div></AppDialog>}
      {!scenarioOnboardingPending && <>
      <div className="studio-next-step" role="region" aria-label="Следующее действие проекта">
        <div>
          <span>Следующий шаг</span>
          <b>{detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status)?'Сценарист работает — дождитесь результата':nextAction.text}</b>
          <small>
            Потрачено по проекту: {formatCredits(spent)} кр. ·{" "}
            {limit === null
              ? "Лимит не задан"
              : `Лимит: ${formatCredits(limit)} кр. · Осталось: ${formatCredits(limit > spent ? limit - spent : 0n)} кр.`}
          </small>
        </div>
        <button className="primary compact" disabled={Boolean(detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status))} onClick={() => selectStage(nextAction.stage)}>
          {detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status)?'В работе':'Продолжить'}
        </button>
      </div>
      <div className="studio-node-rail" aria-label="Этапы мультфильма">
        {studioNodes.map((item) => {
          const state = detail.nodes.find(
            (value) => value.node_key === item.key,
          );
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              className={`${active === item.key ? "active" : ""} ${state?.status ?? ""}`}
              onClick={() => selectStage(item.key)}
            >
              <span>
                <Icon />
              </span>
              <small>{item.label}</small>
              <i />
            </button>
          );
        })}
      </div>
      <details className="studio-workflow-status">
        <summary>Как продолжится производство</summary>
        <p className="studio-mode-boundary">{autoBoundary(detail.nodes)}</p>
      </details>
      <small className="studio-settings-status" role="status">
        {settingsText}
      </small>
      </>}
      {active === "scenario" ? (
        scenarioOnboardingPending ? (
          <>
            {scenarioSourceMode && !scenarioKickoffMode && (
              <div className="scenario-onboarding-model">
                <span>Сценарист</span>
                <Dropdown
                  label="Модель сценариста"
                  value={scenarioModelId}
                  options={scenarioModels.map((model) => ({
                    value: model.id,
                    label: model.name,
                    note: 'Расход по фактическому объёму',
                    group: model.family,
                  }))}
                  onChange={(value) => void chooseScenarioModel(value)}
                  disabled={busy}
                />
              </div>
            )}
            <ScenarioOnboarding
              sourceMode={scenarioSourceMode}
              kickoffMode={scenarioKickoffMode}
              templates={scenarioTemplates}
              busy={busy}
              onChooseSource={(mode, templateId) =>
                void chooseScenarioSource(mode, templateId)
              }
              onChooseKickoff={(mode) => void chooseScenarioKickoff(mode)}
              autoLimit={scenarioQuote?.maxCredits??null}
            />
          </>
        ) : (
          <>
            {detail.scenarioOperation && ['queued','running','reconciling'].includes(detail.scenarioOperation.status) && <div className="scenario-operation" role="status"><b>{detail.scenarioOperation.safeMessage||({queued:'Запрос принят',drafting:'Сценарист собирает вариант',checking:'Проверяем сценарий',repairing:'Дорабатываем найденные недостатки',ready:'Готово',stopped:'Работа приостановлена'}[detail.scenarioOperation.phase])}</b><small>{detail.scenarioOperation.maxAttempts?`Попытка ${Math.max(1,detail.scenarioOperation.attempt??1)} из ${detail.scenarioOperation.maxAttempts}. `:''}Можно закрыть приложение — состояние сохранено на сервере.</small></div>}
            {scenarioKickoffMode === "auto" &&
              detail.scenarioVersions.length === 0 && !(detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status)) && (
                <div className="scenario-onboarding-retry" role="alert">
                  <div>
                    <b>Агент не завершил сценарий</b>
                    <span>
                      {detail.scenarioOperation?.safeMessage || 'Редактируемый сценарий ещё не подготовлен. Можно продолжить работу.'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="primary compact"
                    disabled={busy||Boolean(detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status))}
                    onClick={() => void retryInitialScenario()}
                  >
                    <RefreshCw />
                    {detail.scenarioOperation?.attempt===0?'Повторить запуск':'Достроить сценарий'}
                  </button>
                </div>
              )}
            <ScenarioWorkspaceView
              key={detail.project.id}
              projectId={detail.project.id}
              versions={detail.scenarioVersions}
              messages={scenarioMessages}
              proposals={detail.scenarioProposals ?? []}
              cast={detail.cast ?? []}
              models={scenarioModels}
              modelId={scenarioModelId}
              mode={scenarioNode?.mode ?? "manual"}
              busy={busy||Boolean(detail.scenarioOperation&&['queued','running','reconciling'].includes(detail.scenarioOperation.status))}
              onReload={() => loadDetail(detail.project.id)}
              onModel={chooseScenarioModel}
              onMode={setMode}
              onLocked={lock}
              onNotice={setMessage}
            />
          </>
        )
      ) : active === "montage" ? (
        <FinalReview
          key={detail.project.id}
          project={detail.project}
          scenes={detail.scenes}
          renderJobs={detail.renderJobs ?? []}
          musicAssets={musicAssets}
          userId={userId}
          busy={busy}
          montageConfig={
            detail.nodes.find((item) => item.node_key === "montage")?.config ??
            {}
          }
          onSavePreferences={(config) => persistNodeSettings("montage", config)}
          onReload={async () => {
            await loadDetail(detail.project.id);
            await loadProjects();
            onChanged();
          }}
          onNavigate={(target, sceneId) => {
            selectStage(target);
            setFocusedScene(sceneId ?? null);
            setTimeout(
              () =>
                document
                  .getElementById(sceneId ? `studio-scene-${sceneId}` : "")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" }),
              50,
            );
          }}
          onMessage={setMessage}
        />
      ) : (
        <div className="studio-node-panel">
          <div className="node-panel-head">
            <div>
              <h2>{nodeDefinition.label}</h2>
              <span>
                {node?.safe_error_message ||
                  studioStatus[node?.status ?? ""] ||
                  node?.status}{" "}
                · {finishedScenes}/{eligibleScenes.length} сцен
              </span>
            </div>
            <div className="mode-switch">
              <button
                className={node?.mode === "manual" ? "active" : ""}
                onClick={() => void setMode("manual")}
              >
                Вручную
              </button>
              <button
                className={node?.mode === "auto" ? "active" : ""}
                onClick={() => void setMode("auto")}
              >
                Авто
              </button>
            </div>
          </div>
          <p className="studio-mode-explanation">
            {node?.mode === "auto"
              ? "Авто: после явного подтверждения выполняется только показанная в расчёте цепочка. Сам переключатель бесплатный и ничего не запускает."
              : "Вручную: этот этап ждёт вашего запуска. Режим следующего этапа настраивается отдельно."}
          </p>
          {currentRun && ["active", "paused"].includes(currentRun.state) && (
            <div className="studio-run-state">
              <span>
                Запуск:{" "}
                {currentRun.state === "active"
                  ? "выполняется"
                  : "приостановлен"}
              </span>
              {currentRun.state === "active" && (
                <button
                  type="button"
                  className="secondary compact"
                  onClick={() => void stopRun()}
                >
                  Остановить новые отправки
                </button>
              )}
            </div>
          )}
          {selectedModel && !modelCompatibility && <p className="studio-model-config">Проверяем совместимость моделей для сцен…</p>}{selectedModel && modelCompatibility && (
            <div className="studio-model-config">
              {defaultModelOptions.length===0&&<p>Нет одной модели для всех сцен. Выберите модели в карточках ниже.</p>}
              <Dropdown
                label={`Модель ${nodeDefinition.label}`}
                value={selectedModel.id}
                options={defaultModelOptions.map((model) => ({
                  value: model.id,
                  label: model.displayName,
                  note: `от ${minimumPrice(model)} кр.`,
                  ...(model.family ? { group: model.family } : {}),
                }))}
                onChange={(value) => chooseNodeModel(active, value)}
              />
              <button type="button" className="secondary compact" disabled={busy || !defaultModelOptions.some((item) => item.id === selectedModel.id)} onClick={() => void applyDefaultModelToAll()}>Применить ко всем</button>
              <p>{selectedModel.help?.summary || selectedModel.displayName}</p>
            </div>
          )}
          {active === "voice" && (
            <div className="voice-casting">
              <h3>Голоса проекта</h3>
              <small>
                Бесплатный образец текущего голоса недоступен. Выберите голос из
                списка модели; его звучание можно оценить в готовой озвучке.
              </small>
              <VoicePicker title="Рассказчик" value={narratorVoice} options={voiceOptions}
                  onChange={(value) => {
                    setNarratorVoice(value);
                    void persistNodeSettings(
                      "voice",
                      configFor("voice", { narratorVoice: value }),
                    ).catch(() => undefined);
                  }}
              />
              {(detail.cast ?? []).map((character) => (
                <VoicePicker key={character.id} title={character.roleName || character.characterSnapshot.name || "Персонаж"}
                    value={
                      characterVoices[character.id] ?? FISH_VOICES.find(v=>v.gender===(character.characterSnapshot.gender==='female'?'female':'male'))?.id ?? FISH_DEFAULT_VOICE
                    }
                    options={voiceOptions}
                    onChange={(value) => {
                      const next = {
                        ...characterVoices,
                        [character.id]: value,
                      };
                      setCharacterVoices(next);
                      void persistNodeSettings(
                        "voice",
                        configFor("voice", { characterVoices: next }),
                      ).catch(() => undefined);
                    }}
                />
              ))}
            </div>
          )}
          {startUncertain && !quote && (
            <div className="studio-run-state">
              <span>
                Проверяем ранее отправленный запуск. Новая отправка отключена.
              </span>
              <button
                type="button"
                className="secondary compact"
                disabled={busy}
                onClick={() => void checkUncertainStart()}
              >
                Проверить статус
              </button>
            </div>
          )}
          <button
            className="primary studio-run"
            disabled={
              busy ||
              startUncertain ||
              !["ready", "failed"].includes(node?.status ?? "") ||
              !selectedModel
            }
            onClick={() => void runNode()}
          >
            <Play />
            {node?.status === "running"
              ? "Выполняется"
              : selectedModel
                ? eligibleScenes.length
                  ? `${node?.status === "failed" ? "Повторить ошибки" : "Запустить"} ${pendingScenes.length} сцен · проверить стоимость`
                  : "Завершить этап · 0 кр."
                : "Выберите модель"}
          </button>
          {node?.status === "completed" && (
            <p className="node-complete-note">
              Этап готов. Неудачную сцену можно перегенерировать отдельно ниже.
            </p>
          )}
          <div className="scene-strip">
            {detail.scenes.map((scene) => {
              const hasResult =
                active === "storyboard"
                  ? Boolean(scene.image_media_id)
                  : active === "animation"
                    ? Boolean(scene.animation_media_id)
                    : Boolean(scene.voice_media_id);
              const sceneModels = sceneModelOptions(scene);
              const override = scene.production_overrides?.[active];
              const defaultLabel = selectedModel?.displayName ?? "Модель по умолчанию";const canInherit=Boolean(selectedModel&&sceneModels.some((item)=>item.id===selectedModel.id));const sceneDropdownOptions=sceneModels.map((model)=>({value:model.id,label:model.displayName,note:"от "+minimumPrice(model)+" кр."}));sceneDropdownOptions.unshift(canInherit?{value:"",label:"По умолчанию · "+defaultLabel,note:""}:{value:"",label:"Выберите модель для этой сцены",note:""});
              return (
                <article
                  id={`studio-scene-${scene.id}`}
                  className={`${scene.scene_type === "breather" ? "breather" : ""} ${focusedScene === scene.id ? "focused" : ""}`}
                  key={scene.id}
                >
                  <button
                    type="button"
                    className="scene-media"
                    aria-label={`Открыть сцену ${scene.position + 1}`}
                    onClick={() => setInspectedScene(scene)}
                  >
                    {active === "storyboard" && scene.image_media_id ? (
                      <img
                        loading="eager"
                        decoding="async"
                        src={`/api/media/${scene.image_media_id}/thumbnail`}
                        alt={`Кадр сцены ${scene.position + 1}: ${scene.title}`}
                      />
                    ) : active === "animation" && scene.animation_media_id ? (
                      <video
                        src={`/api/media/${scene.animation_media_id}/content`}
                      />
                    ) : active === "voice" && scene.voice_media_id ? (
                      <audio
                        src={`/api/media/${scene.voice_media_id}/content`}
                      />
                    ) : (
                      <span>
                        {scene.scene_type === "breather"
                          ? "Титр"
                          : scene.position + 1}
                      </span>
                    )}
                  </button>
                  <b>
                    {scene.position + 1}. {scene.title}
                  </b>
                  <small>
                    {Number(scene.duration_ms) / 1000} сек. ·{" "}
                    {scene.scene_type === "breather" && active !== "voice"
                      ? "титр без генерации изображения"
                      : (studioStatus[scene.status] ?? scene.status)}
                  </small>
                  {scene.shot_plan && <small className="scene-shot-plan">Кадр: {shotPlanLabels[scene.shot_plan.kind]??scene.shot_plan.kind}{scene.shot_plan.focus?" · "+scene.shot_plan.focus:""}</small>}
                  {scene.scene_type !== "breather" && ["storyboard","animation","voice"].includes(active) && (!modelCompatibility?<small>Проверяем совместимость моделей…</small>:sceneModels.length?<Dropdown label={"Модель сцены "+(scene.position+1)} className="scene-model-picker" value={override?.modelId ?? ""} options={sceneDropdownOptions} onChange={value=>void chooseSceneModel(scene,value)}/>:<small>Нет подходящей модели для этой сцены. Проверьте референсы или формат.</small>)}
                  {canTarget(scene) &&
                    (hasResult || scene.status === "failed") && (
                      <button
                        type="button"
                        className="secondary compact scene-regenerate"
                        disabled={busy || node?.status === "running"}
                        onClick={() => void runNode(scene.id)}
                      >
                        <RefreshCw />
                        {regenerateLabel(scene)}
                      </button>
                    )}
                  {active === "storyboard" &&
                    scene.scene_type !== "breather" && (
                      <StoryboardCastStrip
                        cast={detail.cast ?? []}
                        assignments={(detail.sceneCast ?? []).filter(
                          (item) => item.scene_id === scene.id,
                        )}
                        onEdit={() => openSceneCast(scene.id)}
                      />
                    )}
                </article>
              );
            })}
          </div>
        </div>
      )}
      {quote && (
        <AppDialog
          title="Проверка запуска"
          onClose={() => {
            setQuote(null);
          }}
        >
          <div className="studio-preflight">
            <p>
              <b>
                {quoteScope?.scope === "scene"
                  ? "Одна сцена"
                  : quoteScope?.scope === "failed"
                    ? "Только последние ошибки"
                    : "Пакет сцен"}
              </b>{" "}
              · {quote.stopBoundary}
            </p>
            {quote.steps.map((step) => (
              <section key={step.node}>
                <h3>
                  {stageNames[step.node as keyof typeof stageNames] ??
                    step.node}
                </h3>
                {step.jobs.map((job) => {
                  const sceneItem = detail.scenes.find(
                    (item) => item.id === job.sceneId,
                  );
                  const modelItem = models.find(
                    (item) => item.id === (job.modelId ?? step.modelId),
                  );
                  return (
                    <div
                      className="studio-quote-scene"
                      key={`${step.node}:${job.sceneId}`}
                    >
                      <b>
                        {sceneItem
                          ? `${sceneItem.position + 1}. ${sceneItem.title}`
                          : job.sceneId}
                      </b>
                      <small>
                        {modelItem?.displayName ?? job.modelId ?? step.modelId} ·{" "}
                        {job.inputMode} ·{" "}
                        {job.outputResolution
                          ? `${job.outputResolution} · `
                          : ""}
                        {step.node === "animation"
                          ? job.generateAudio
                            ? "со звуком · "
                            : "без звука · "
                          : ""}
                        {job.durationSeconds
                          ? `${job.durationSeconds} сек. · `
                          : ""}
                        {job.references.length} реф. ·{" "}
                        {formatCredits(BigInt(job.estimatedUnits))} кр.
                      </small>
                      {job.references.length > 0 && (
                        <small>
                          Материалы: {job.references.map((item) => item.id).join(", ")}
                        </small>
                      )}
                      {Object.keys(job.parameters).length > 0 && (
                        <small>
                          Параметры: {JSON.stringify(job.parameters)}
                        </small>
                      )}
                      {job.voiceAssignment &&
                        Object.keys(job.voiceAssignment).length > 0 && (
                          <small>
                            Голоса: {voiceAssignmentSummary(job.voiceAssignment,voiceOptions)}
                          </small>
                        )}
                    </div>
                  );
                })}
              </section>
            ))}
            {quote.excluded.length > 0 && (
              <details>
                <summary>Не войдут в запуск: {quote.excluded.length}</summary>
                {quote.excluded.map((item) => (
                  <p key={item.sceneId}>
                    {detail.scenes.find(
                      (sceneItem) => sceneItem.id === item.sceneId,
                    )?.title ?? item.sceneId}
                    : {item.reason}
                  </p>
                ))}
              </details>
            )}
            {quote.blockers.map((item) => (
              <Notice key={item} text={item} />
            ))}
            {quote.warnings.map((item) => (
              <Notice key={item} text={item} />
            ))}
            {(quote.retainedSelections?.length ?? 0) > 0 && (
              <details>
                <summary>
                  Сохранённые выбранные результаты: {quote.retainedSelections!.length}
                </summary>
                {quote.retainedSelections!.map((item) => (
                  <p key={`${item.sceneId}:${item.node}`}>
                    {item.position + 1}. {item.title} · {item.node} · {item.freshness}
                  </p>
                ))}
              </details>
            )}
            {startUncertain && (
              <Notice text="Ответ запуска потерян. Повторная отправка отключена; можно только проверить статус по сохранённому ключу." />
            )}
            <button
              className="primary"
              disabled={busy || quote.blockers.length > 0 || startUncertain}
              onClick={() => void confirmStart()}
            >
              Запустить · {quote.totals.credits} кр.
            </button>
            {startUncertain && (
              <button
                className="secondary"
                disabled={busy}
                onClick={() => void checkUncertainStart()}
              >
                Проверить статус
              </button>
            )}
            <small>
              Это подтверждение относится только к показанным сценам и этапам.
              Изменение настроек потребует нового расчёта.
            </small>
          </div>
        </AppDialog>
      )}
      {inspectedScene && (
        <AppDialog
          title={`Сцена ${inspectedScene.position + 1}: ${inspectedScene.title}`}
          onClose={() => setInspectedScene(null)}
        >
          <div className="studio-scene-inspector">
            <>
              {inspectedScene.image_media_id && (
                <img
                  src={`/api/media/${inspectedScene.image_media_id}/content`}
                  alt={`Кадр сцены ${inspectedScene.position + 1}`}
                />
              )}{" "}
              {inspectedScene.animation_media_id && (
                <video
                  controls
                  playsInline
                  src={`/api/media/${inspectedScene.animation_media_id}/content`}
                />
              )}{" "}
              {inspectedScene.voice_media_id && (
                <audio
                  controls
                  src={`/api/media/${inspectedScene.voice_media_id}/content`}
                />
              )}{" "}
              {!inspectedScene.image_media_id &&
                !inspectedScene.animation_media_id &&
                !inspectedScene.voice_media_id && (
                  <p className="muted">
                    Для этой сцены ещё нет готового результата.
                  </p>
                )}
            </>
            <p>{inspectedScene.description}</p>
            {inspectedScene.narration && (
              <p>
                <b>Текст:</b> {inspectedScene.narration}
              </p>
            )}
            {inspectedScene.dialogue?.map((line, index) => (
              <p key={index}>
                <b>{line.speaker}:</b> {line.text}
              </p>
            ))}
            <div className="scene-inspector-cast">
              {(detail.sceneCast ?? [])
                .filter((item) => item.scene_id === inspectedScene.id)
                .map((item) => {
                  const character = (detail.cast ?? []).find(
                    (entry) => entry.id === item.project_character_id,
                  );
                  const look = selectedLookName(
                    character,
                    item.selected_look_id,
                  );
                  return (
                    <span key={item.id}>
                      {character?.roleName ||
                        character?.characterSnapshot.name ||
                        "Персонаж"}
                      {look ? ` · образ: ${look}` : ""}
                    </span>
                  );
                })}
            </div>
            <section className="studio-variants">
              <h3>Варианты результата</h3>
              {variantsBusy && <small>Загружаем варианты…</small>}
              {variants?.variants.map((variant, index) => (
                <div
                  className={`${variant.selected ? "selected" : ""} ${variant.compatible ? "" : "stale"}`}
                  key={`${variant.jobId}:${variant.mediaId ?? index}`}
                >
                  <span>
                    <b>Вариант {index + 1}</b>
                    <small>
                      {variant.status === "completed"
                        ? variant.selected && variants.selection?.freshness !== "current"
                          ? "Выбран ранее, но входные данные изменились"
                          : variant.compatible
                            ? "Совместим с текущим запуском"
                            : "Создан с другими входными данными"
                        : variant.errorMessage ||
                          studioStatus[variant.status] ||
                          variant.status}
                    </small>
                  </span>
                  {variant.mediaId && (
                    <a
                      href={`/api/media/${variant.mediaId}/content`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Открыть
                    </a>
                  )}
                  {variant.mediaId &&
                    (!variant.selected || variants.selection?.freshness !== "current") && (
                    <button
                      type="button"
                      className="secondary compact"
                      disabled={busy || !variant.compatible}
                      aria-label={`${variant.selected ? "Подтвердить снова" : "Выбрать"} вариант ${index + 1}`}
                      onClick={() => void selectVariant(variant)}
                    >
                      Выбрать
                    </button>
                  )}
                  {variant.selected && <em>Выбран</em>}
                </div>
              ))}
              {!variantsBusy && variants?.variants.length === 0 && (
                <small>Вариантов пока нет.</small>
              )}
            </section>
            {canTarget(inspectedScene) && (
              <button
                className="secondary"
                disabled={busy || node?.status === "running"}
                onClick={() => void runNode(inspectedScene.id)}
              >
                <RefreshCw />
                {regenerateLabel(inspectedScene)}
              </button>
            )}
          </div>
        </AppDialog>
      )}
      {editingScene && (
        <SceneCastEditor
          projectId={detail.project.id}
          sceneId={editingScene}
          cast={detail.cast ?? []}
          assignments={(detail.sceneCast ?? []).filter(
            (item) => item.scene_id === editingScene,
          )}
          onClose={closeSceneCast}
          onSaved={() => loadDetail(detail.project.id)}
          onError={setMessage}
        />
      )}{" "}
      {templateOpen && (
        <Modal
          title="Сохранить настройки как шаблон"
          close={() => history.back()}
        >
          <label>
            Название шаблона
            <input
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="Например, Добрая история"
            />
          </label>
          <p className="muted">
            Это шаблон производственных настроек: режимов и моделей этапов.
            Шаблон сценария — отдельный материал внутри раздела «Сценарий».
          </p>
          <button
            className="primary"
            disabled={!templateName.trim() || busy}
            onClick={() => void saveTemplate()}
          >
            Сохранить
          </button>
        </Modal>
      )}
    </section>
  );
}
